/**
 * First-load notice. The first time a structure is loaded the app asks the user to acknowledge that this is
 * Nanome Lite (Beta); the answer is kept in localStorage so it is asked once per browser. Viewer.vue installs
 * `ensureAccepted` as the store's load guard and mounts BetaNoticeDialog, which resolves the pending request.
 */
import { ref } from 'vue';

const KEY = 'nanome-lite.beta-notice.accepted';
/** Bump when the notice text changes in a way that needs a fresh acknowledgement. */
const VERSION = '1';

export const REPORT_BUG_URL = 'mailto:support@nanome.ai?subject=' + encodeURIComponent('Nanome Lite (Beta) bug report');
export const TERMS_URL = 'https://nanome.ai/terms';
export const FULL_NANOME_URL = 'https://app.nanome.ai';

const visible = ref(false);
let pending: Promise<boolean> | null = null;
let settle: ((accepted: boolean) => void) | null = null;

function read(): boolean {
  try { return localStorage.getItem(KEY) === VERSION; } catch { return false; }
}

export function hasAcceptedBetaNotice(): boolean { return read(); }

/** Resolves true once the user has accepted (immediately when already accepted), false when they cancel. */
export function ensureAccepted(): Promise<boolean> {
  if (read()) return Promise.resolve(true);
  if (!pending) {
    pending = new Promise<boolean>((resolve) => { settle = resolve; });
    visible.value = true;
  }
  return pending;
}

function finish(accepted: boolean) {
  if (accepted) { try { localStorage.setItem(KEY, VERSION); } catch { /* storage unavailable: ask again next time */ } }
  visible.value = false;
  settle?.(accepted);
  settle = null;
  pending = null;
}

export function useBetaNotice() {
  return { visible, accept: () => finish(true), cancel: () => finish(false) };
}

/** Test helper. */
export function resetBetaNotice() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  visible.value = false; settle = null; pending = null;
}
